import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exchangeConnectorOAuthCode } from '../services/api';
import { CheckCircle, XCircle, Loader, ArrowLeft, RotateCcw } from 'lucide-react';

/**
 * Handles the OAuth redirect callback for connector authorization.
 *
 * Flow:
 * 1. User clicks "Connect" on a connector → POST /connectors/{id}/authorize → redirects to provider
 * 2. Provider redirects back here: /connectors/oauth/callback?code=AUTH_CODE&state=BASE64_STATE
 * 3. We decode the state (connectorId|tenantId|userId), exchange the code via apiFetch
 * 4. Redirect back to the Connectors page with success/error indicator
 *
 * Uses the app's apiFetch infrastructure for:
 * - Correct API URL resolution (same as rest of app)
 * - Automatic JWT refresh if token expired during provider redirect
 * - Consistent error handling, timeout, and retry logic
 * - X-Tenant-Id header injection
 */
const ConnectorOAuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [message, setMessage] = useState('Exchanging authorization code...');
  const [providerEmail, setProviderEmail] = useState(null);
  const [connectorId, setConnectorId] = useState(null);
  const exchangeAttempted = useRef(false); // prevent double-execution in StrictMode

  useEffect(() => {
    // Guard against React StrictMode double-mount or re-renders
    if (exchangeAttempted.current) return;
    exchangeAttempted.current = true;

    const code = searchParams.get('code');
    const stateParam = searchParams.get('state');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // ── Provider denied authorization ──
    if (error) {
      setStatus('error');
      setMessage(errorDescription || `Provider denied authorization: ${error}`);
      return;
    }

    // ── Missing required parameters ──
    if (!code) {
      setStatus('error');
      setMessage('Missing authorization code. The provider may not have granted access.');
      return;
    }
    if (!stateParam) {
      setStatus('error');
      setMessage('Missing state parameter. This may be a CSRF attack or a stale redirect.');
      return;
    }

    // ── Decode state: base64url(connectorId|tenantId|userId) ──
    let parsedConnectorId;
    try {
      // Handle both standard base64 and base64url encoding
      const normalized = stateParam.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(normalized);
      const parts = decoded.split('|');
      parsedConnectorId = parts[0];

      if (!parsedConnectorId) {
        throw new Error('Connector ID not found in state');
      }

      // Validate it looks like a UUID
      const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRe.test(parsedConnectorId)) {
        throw new Error(`Invalid connector ID format: ${parsedConnectorId.substring(0, 20)}`);
      }

      setConnectorId(parsedConnectorId);
    } catch (e) {
      setStatus('error');
      setMessage(`Invalid callback state: ${e.message}`);
      return;
    }

    // ── Exchange authorization code for tokens via apiFetch ──
    // apiFetch handles: correct API_URL, JWT refresh, timeout, tenant header, error normalization
    exchangeConnectorOAuthCode(parsedConnectorId, code)
      .then((result) => {
        if (result?.ok && result.data?.status === 'connected') {
          setStatus('success');
          setProviderEmail(result.data.providerEmail || null);
          setMessage('Account connected successfully!');
          // Auto-redirect after 2.5 seconds
          setTimeout(() => {
            navigate('/connectors', { state: { connectorConnected: parsedConnectorId } });
          }, 2500);
        } else {
          // apiFetch returned { ok: false } or unexpected data shape
          const errorMsg = result?.data?.error
            || result?.error
            || `Token exchange failed (unexpected response)`;
          setStatus('error');
          setMessage(errorMsg);
        }
      })
      .catch((err) => {
        // apiFetch throws on HTTP errors (401, 403, 5xx, network failures)
        const msg = err.message || 'Unknown error';
        if (msg.includes('Unauthorized') || msg.includes('401') || msg.includes('403')) {
          setStatus('error');
          setMessage(
            'Your session expired while you were on the provider\'s consent screen. '
            + 'Please log in again and retry the connection.'
          );
        } else if (msg.includes('abort') || msg.includes('timeout')) {
          setStatus('error');
          setMessage('Request timed out. The backend may be unreachable. Please try again.');
        } else {
          setStatus('error');
          setMessage(`Connection failed: ${msg}`);
        }
      });
  }, []);

  // ── Retry handler ──
  const handleRetry = () => {
    if (connectorId) {
      // Navigate back to connectors so user can click "Connect" again
      navigate('/connectors', { state: { retryConnector: connectorId } });
    } else {
      navigate('/connectors');
    }
  };

  // ── Styles ──
  const containerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', background: '#fafaf9', fontFamily: "'Inter', system-ui, sans-serif",
  };
  const cardStyle = {
    background: 'white', borderRadius: 12, padding: '40px 48px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
    maxWidth: 440, width: '100%', textAlign: 'center',
  };
  const headingStyle = { fontSize: 18, fontWeight: 600, color: '#18181b', margin: '0 0 8px' };
  const subtextStyle = { fontSize: 13, color: '#71717a', margin: 0, lineHeight: 1.6 };
  const btnStyle = {
    padding: '8px 20px', borderRadius: 6, border: 'none',
    fontSize: 13, fontWeight: 500, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {status === 'processing' && (
          <>
            <Loader size={40} style={{ color: '#0d9488', animation: 'spin 1s linear infinite', marginBottom: 16 }} />
            <h2 style={headingStyle}>Connecting Account</h2>
            <p style={subtextStyle}>{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle size={40} style={{ color: '#16a34a', marginBottom: 16 }} />
            <h2 style={headingStyle}>Connected!</h2>
            {providerEmail && (
              <p style={{ fontSize: 14, color: '#0d9488', margin: '0 0 8px', fontWeight: 500 }}>
                {providerEmail}
              </p>
            )}
            <p style={subtextStyle}>Redirecting to Connectors page...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle size={40} style={{ color: '#ef4444', marginBottom: 16 }} />
            <h2 style={headingStyle}>Connection Failed</h2>
            <p style={{ ...subtextStyle, marginBottom: 20 }}>{message}</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={handleRetry} style={{ ...btnStyle, background: '#0d9488', color: 'white' }}>
                <RotateCcw size={13} /> Try Again
              </button>
              <button onClick={() => navigate('/connectors')}
                style={{ ...btnStyle, background: 'white', color: '#52525b', border: '1px solid #e4e4e7' }}>
                <ArrowLeft size={13} /> Back to Connectors
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default ConnectorOAuthCallbackPage;
